/**
 * InlineSubmitButton — unified strict-naming upload control.
 *
 * COMPACT version of MyTasksRow's submit cluster. Shared by:
 *   • FreelancerDashboardNew  (freelancer / vendor_artist / vendor_team_manager)
 *   • VendorTasksPage         (vendor_producer)
 *   • EmployeeDashboard       (employee)
 *
 * Founder spec May 24, 2026: every artist gets the SAME submit experience —
 *   • Dual buttons: UPLOAD CBA CLIP (video) | UPLOAD SHOT (scene file)
 *   • LAY/ANM stage dropdown for animator tasks, locked until LAY approved
 *   • Strict canonical filename + project FPS + project resolution + DAG gate
 *   • Every denial shows a `help_tip` so artists know how to fix
 *   • Auto-chains /reviews/upload-media → /reviews/submit so the bytes
 *     become a tracked review_submissions row
 */
import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Check, AlertTriangle, Film, FileBox, Lightbulb } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

export default function InlineSubmitButton({ task, onSubmitted, testidPrefix = 'inline-submit' }) {
  const { api } = useAuth();
  const cbaRef = useRef(null);
  const shotRef = useRef(null);
  const fileRef = useRef(null);
  const [canonical, setCanonical] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [ok, setOk] = useState(false);
  const [uploadMode, setUploadMode] = useState(null);

  // Founder spec May 24, 2026 — per-discipline upload UX:
  //   • Animatics / Storyboard / Edit / Sound / Final → SINGLE file button
  //   • Animator (ANM) → dual CBA Clip + Shot with LAY/ANM dropdown
  //   • Everyone else → dual CBA Clip + Shot
  const taskTypeRaw = (task.task_type || task.stage || '').toString().toLowerCase();
  const SINGLE_FILE_DISCIPLINES = new Set([
    'animatics', 'animatic', 'storyboard', 'storyboarding',
    'edit', 'editing', 'sound', 'audio', 'sfx', 'final',
  ]);
  const DISCIPLINE_ACCEPT = {
    animatics:   { accept: '.mp4,.mov', maxMb: 200 },
    storyboard:  { accept: '.pdf,.png,.jpg,.jpeg,.zip', maxMb: 200 },
    edit:        { accept: '.mp4,.mov,.webm,.mkv', maxMb: 500 },
    sound:       { accept: '.wav,.mp3,.aiff,.flac,.zip', maxMb: 200 },
    final:       { accept: '.mp4,.mov,.mkv', maxMb: 500 },
    asset_build: { accept: '.blend,.fbx,.obj,.usd,.glb,.zip', maxMb: 500 },
    rigging:     { accept: '.blend,.ma,.mb', maxMb: 500 },
    lighting:    { accept: '.png,.jpg,.jpeg,.exr,.tif,.zip', maxMb: 500 },
    compositing: { accept: '.png,.jpg,.jpeg,.exr,.tif,.mp4,.mov,.zip', maxMb: 500 },
    fx:          { accept: '.blend,.hip,.hipnc,.usd,.zip', maxMb: 500 },
  };
  const isSingleFile = SINGLE_FILE_DISCIPLINES.has(taskTypeRaw);
  const accepts = DISCIPLINE_ACCEPT[taskTypeRaw] || { accept: '.mp4,.mov,.blend,.zip', maxMb: 500 };

  // Animator stage dropdown (founder spec May 24, 2026)
  const intrinsicCode = (canonical?.task_code
    || (task.task_type || task.stage || '').toString().toUpperCase()
  ).slice(0, 5);
  const isAnimator = (intrinsicCode === 'ANM' || intrinsicCode === 'ANIMA') && !isSingleFile;
  const [stagePick, setStagePick] = useState('LAY');
  const [upstream, setUpstream] = useState(null);

  // Fetch canonical filename
  useEffect(() => {
    let cancel = false;
    setCanonical(null);
    api.get(`/nomenclature/canonical-for-task/${task.id}`)
      .then(r => { if (!cancel) setCanonical(r.data); })
      .catch(e => { if (!cancel) setCanonical({ error: e?.response?.data?.detail || 'Unable to compute canonical name' }); });
    return () => { cancel = true; };
  }, [api, task.id]);

  // Animator: check upstream LAY approval status
  useEffect(() => {
    if (!isAnimator) { setUpstream(null); return; }
    const parent = task.shot_code || task.entity_code || task.parent_code || '';
    if (!parent) return;
    let cancel = false;
    api.get('/reviews/upstream-status', { params: {
      project_id: task.project_id,
      parent_code: parent,
      task_code: 'ANM',
    }}).then(r => { if (!cancel) setUpstream(r.data); })
      .catch(() => { if (!cancel) setUpstream({ can_submit: false, missing_upstream: ['LAY'] }); });
    return () => { cancel = true; };
  }, [api, isAnimator, task.project_id, task.shot_code, task.entity_code, task.parent_code]);

  // Default the stage dropdown
  useEffect(() => {
    if (isAnimator && upstream) setStagePick(upstream.can_submit ? 'ANM' : 'LAY');
  }, [isAnimator, upstream]);

  const onPickCba = (e) => {
    const f = e.target.files?.[0];
    if (cbaRef.current) cbaRef.current.value = '';
    if (f) doUpload(f, 'cba');
  };
  const onPickShot = (e) => {
    const f = e.target.files?.[0];
    if (shotRef.current) shotRef.current.value = '';
    if (f) doUpload(f, 'shot');
  };

  const doUpload = async (file, mode) => {
    setBusy(true); setErr(null); setOk(false); setUploadMode(mode);
    try {
      const fd = new FormData();
      fd.append('project_id', task.project_id);
      fd.append('file', file);
      fd.append('task_id', task.id);
      fd.append('enforce_canonical', 'true');
      const stage = (task.stage || task.task_type || '').toLowerCase();
      fd.append('stage', stage);
      fd.append('clip_kind', mode === 'cba' ? 'cba_clip' : mode === 'single' ? 'single' : 'shot');
      if (isAnimator) fd.append('stage_override', stagePick);
      const parentCode = task.shot_code || task.entity_code || task.parent_code || '';
      fd.append('parent_code', parentCode);
      const isVid = (file.type || '').startsWith('video/')
        || /\.(mp4|mov|webm|m4v|mkv)$/i.test(file.name);
      fd.append('preset', isVid ? 'web-720' : 'none');

      const up = await api.post('/reviews/upload-media', fd);
      const m = up.data || {};

      // Chain /reviews/submit so the upload becomes a tracked submission.
      // Founder bug Feb 2026 (vendor Sonia, The Little Mermaid Animatics):
      // backend used to refuse with HTTP 409 "Duplicate submission" when
      // the artist already had an in-review row for the same task. The old
      // catch-block here silently swallowed that error and showed a green
      // "SENT" badge — so v2 was lost in the void. The backend has been
      // fixed to auto-resubmit (bump round + archive prev media + reset
      // gates) instead of refusing, so the 409 path should never trigger
      // again. We still keep a defensive log for the rare legacy case.
      const effectiveStage = isAnimator
        ? (stagePick === 'LAY' ? 'layout' : 'animation')
        : stage;
      const kind = parentCode && /^SH\d+/i.test(parentCode) ? 'shot' : 'asset';
      let submissionResponse;
      try {
        const subResp = await api.post('/reviews/submit', {
          project_id: task.project_id,
          kind,
          stage: effectiveStage,
          item_refs: [task.id],
          parent_code: parentCode,
          title: file.name,
          description: `Submitted via MY TASKS · ${effectiveStage.toUpperCase()}`,
          media: [{
            storage_key: m.storage_key, url: m.url,
            type: m.type || (isVid ? 'video' : 'file'),
            filename: m.filename || file.name, size: m.size || file.size,
            thumbnail_url: m.thumbnail_url || '',
            duration_sec: m.transcode?.duration_sec || null,
          }],
        });
        submissionResponse = subResp.data;
      } catch (subErr) {
        const dd = subErr?.response?.data?.detail;
        // The backend's new behaviour is to AUTO-RESUBMIT instead of
        // returning 409. If we still see a "Duplicate submission" string
        // here, the backend deploy is behind — surface a clear error so
        // the artist knows to use the explicit "Resubmit" CTA, instead of
        // silently saying "SENT" with no row created.
        if (typeof dd === 'string' && dd.startsWith('Duplicate submission')) {
          throw new Error(
            'Another submission already exists for this task. Please open it from Approvals and click Resubmit, ' +
            'or contact your PM to clear the active round.',
          );
        }
        throw subErr;
      }

      setOk(true);
      // Founder UX hint Feb 2026: when the backend auto-bumps the round
      // (because the artist already had an in-review row), tell them so
      // they don't wonder "why didn't a new row appear in my list?".
      if (submissionResponse?.auto_resubmitted) {
        const r = submissionResponse.round || '?';
        // toast lives on the parent (no global toast here) — we expose
        // it via a console hint AND via the onSubmitted handler so the
        // parent can re-fetch and surface the new round visibly.
        console.info(
          `[VOS] Auto-resubmitted as round ${r} on existing submission ${submissionResponse.id?.slice(0, 8)}…`
        );
      }
      onSubmitted?.(submissionResponse);
      setTimeout(() => setOk(false), 4000);
    } catch (e2) {
      const d = e2?.response?.data?.detail;
      if (d && typeof d === 'object' && d.error?.startsWith('filename_')) {
        setErr({ kind: d.error, message: d.message, expected_example: d.expected_example, filename: d.filename, help_tip: d.help_tip });
      } else if (d && typeof d === 'object' && d.error === 'dag_blocked') {
        setErr({ kind: 'dag_blocked', message: d.message, missing_labels: d.missing_labels, help_tip: d.help_tip });
      } else if (d && typeof d === 'object' && d.error === 'video_spec_mismatch') {
        setErr({ kind: 'video_spec_mismatch', message: d.message, issues: d.issues, help_tip: d.help_tip });
      } else {
        setErr({ kind: 'generic', message: (typeof d === 'string' ? d : d?.message) || e2?.message || 'Upload failed' });
      }
    } finally {
      setBusy(false);
      setUploadMode(null);
    }
  };

  const expectedNoCanonical = canonical?.error;
  const animLocked = isAnimator && upstream && !upstream.can_submit && stagePick === 'ANM';

  const onPickSingle = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (fileRef.current) fileRef.current.value = '';
    if (f.size > accepts.maxMb * 1024 * 1024) {
      setErr({ kind: 'size', message: `File is ${(f.size/1048576).toFixed(1)} MB — max ${accepts.maxMb} MB for ${taskTypeRaw}.`, help_tip: `Re-export at a smaller bitrate to fit under ${accepts.maxMb} MB.` });
      return;
    }
    doUpload(f, 'single');
  };

  return (
    <div className="relative inline-flex items-center gap-1.5">
      {/* Single-file disciplines (Animatics / Storyboard / Edit / Sound /
          Final) get ONE upload button. Founder spec May 24, 2026. */}
      {isSingleFile ? (
        <>
          <input
            ref={fileRef}
            type="file"
            className="hidden"
            onChange={onPickSingle}
            data-testid={`${testidPrefix}-${task.id}-single-file`}
            accept={accepts.accept}
          />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={busy || !!expectedNoCanonical}
            data-testid={`${testidPrefix}-${task.id}-single-btn`}
            title={`Upload (max ${accepts.maxMb} MB · ${accepts.accept})`}
            className="h-7 px-2 bg-emerald-500 hover:bg-emerald-400 text-zinc-950 text-[9px] font-bold uppercase tracking-wider rounded-sm flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" />
              : ok ? <Check className="w-3 h-3" />
              : <Film className="w-3 h-3" />}
            {busy ? 'Up…' : ok ? 'Sent' : 'Upload'}
          </button>
        </>
      ) : (
        <>
          {isAnimator && (
            <select
              value={stagePick}
              onChange={e => setStagePick(e.target.value)}
              disabled={busy}
              data-testid={`${testidPrefix}-${task.id}-stage`}
              className="h-7 px-1.5 rounded-sm bg-zinc-900 border border-zinc-700 text-[9px] font-semibold text-white uppercase tracking-wider focus:outline-none disabled:opacity-50"
              title={upstream && !upstream.can_submit ? 'Animation locked until Layout is approved' : 'Pick LAY or ANM'}
            >
              <option value="LAY">LAY</option>
              <option value="ANM" disabled={upstream && !upstream.can_submit}>
                {upstream && !upstream.can_submit ? 'ANM 🔒' : 'ANM'}
              </option>
            </select>
          )}
          <input
            ref={cbaRef}
            type="file"
            className="hidden"
            onChange={onPickCba}
            data-testid={`${testidPrefix}-${task.id}-cba-file`}
            accept=".mp4,.mov,.webm,.mkv,.m4v"
          />
          <input
            ref={shotRef}
            type="file"
            className="hidden"
            onChange={onPickShot}
            data-testid={`${testidPrefix}-${task.id}-shot-file`}
            accept={accepts.accept}
          />
          <button
            onClick={() => cbaRef.current?.click()}
            disabled={busy || !!expectedNoCanonical || animLocked}
            data-testid={`${testidPrefix}-${task.id}-cba-btn`}
            title="Upload a playblast / preview clip"
            className="h-7 px-2 bg-violet-500 hover:bg-violet-400 text-zinc-950 text-[9px] font-bold uppercase tracking-wider rounded-sm flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
          >
        {busy && uploadMode === 'cba' ? <Loader2 className="w-3 h-3 animate-spin" />
          : ok && uploadMode === 'cba' ? <Check className="w-3 h-3" />
          : <Film className="w-3 h-3" />}
        {busy && uploadMode === 'cba' ? 'Up…' : ok && uploadMode === 'cba' ? 'Sent' : 'CBA Clip'}
      </button>
      <button
        onClick={() => shotRef.current?.click()}
        disabled={busy || !!expectedNoCanonical || animLocked}
        data-testid={`${testidPrefix}-${task.id}-shot-btn`}
        title="Upload the working scene file"
        className="h-7 px-2 bg-emerald-500 hover:bg-emerald-400 text-zinc-950 text-[9px] font-bold uppercase tracking-wider rounded-sm flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {busy && uploadMode === 'shot' ? <Loader2 className="w-3 h-3 animate-spin" />
          : ok && uploadMode === 'shot' ? <Check className="w-3 h-3" />
          : <FileBox className="w-3 h-3" />}
        {busy && uploadMode === 'shot' ? 'Up…' : ok && uploadMode === 'shot' ? 'Sent' : 'Shot'}
      </button>
        </>
      )}
      {err && (
        <div
          className="absolute right-0 top-full mt-1 z-40 w-80 bg-zinc-950 border border-rose-500/40 rounded p-2 text-[10px] shadow-xl"
          data-testid={`${testidPrefix}-${task.id}-error`}
        >
          <div className="flex items-start gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5 text-rose-400 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-rose-200 font-semibold">{err.message || 'Upload failed'}</p>
              {err.kind === 'dag_blocked' && err.missing_labels && (
                <p className="text-zinc-300 mt-0.5">Wait for: <code className="font-mono text-amber-300">{err.missing_labels.join(', ')}</code> approval.</p>
              )}
              {err.kind === 'video_spec_mismatch' && (err.issues || []).map((it, i) => (
                <p key={i} className="text-zinc-300 mt-0.5">• {it}</p>
              ))}
              {err.expected_example && (
                <p className="text-emerald-300 mt-0.5">Rename to: <code className="font-mono">{err.expected_example}</code></p>
              )}
              {err.filename && (
                <p className="text-zinc-500 mt-0.5 truncate" title={err.filename}>You sent: <code className="font-mono">{err.filename}</code></p>
              )}
              {err.help_tip && (
                <div className="mt-1.5 flex items-start gap-1.5 p-1.5 rounded-sm bg-amber-500/10 border border-amber-500/30" data-testid={`${testidPrefix}-${task.id}-help-tip`}>
                  <Lightbulb className="w-3 h-3 text-amber-400 shrink-0 mt-0.5" />
                  <p className="text-amber-200 leading-snug">
                    <span className="font-semibold uppercase tracking-wider mr-1">Tip:</span>
                    {err.help_tip}
                  </p>
                </div>
              )}
              <button onClick={() => setErr(null)} className="text-zinc-500 hover:text-zinc-300 mt-1 text-[9px] uppercase tracking-wider">dismiss</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
